import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight, Send } from "lucide-react";
import api from "../lib/api";
import { SITE } from "../lib/content";

const REASONS = [
  "Speaking inquiry",
  "Media / Press",
  "Foundation partnership",
  "Book reading or event",
  "General",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: REASONS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [nlLoading, setNlLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message received.");
      setForm({ name: "", email: "", subject: REASONS[0], message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : Array.isArray(d)
          ? d.map((x) => x.msg).join(" ")
          : "Could not send. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setNlLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email: newsletterEmail });
      toast.success(data.message || "Subscribed.");
      setNewsletterEmail("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not subscribe.");
    } finally {
      setNlLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-cream">
      {/* Header */}
      <section className="container-vk pt-20 md:pt-28 pb-16">
        <p className="overline mb-6">Contact</p>
        <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight max-w-4xl">
          For{" "}
          <span className="italic text-burgundy">
            speaking, press, partnerships
          </span>{" "}
          &mdash; or a question worth answering.
        </h1>
        <p className="mt-8 text-lg text-ink/70 max-w-2xl leading-relaxed">
          The form below is read personally. For time-sensitive media requests,
          please indicate the deadline in your message.
        </p>
      </section>

      <section className="container-vk pb-24 grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-8 space-y-6 border border-paperline p-8 md:p-12 bg-cream"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <Field
              label="Your Name"
              required
              value={form.name}
              onChange={update("name")}
              testid="contact-name"
            />
            <Field
              label="Email Address"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              testid="contact-email"
            />
          </div>

          <div>
            <label className="overline mb-3 block">Reason for contact</label>
            <select
              value={form.subject}
              onChange={update("subject")}
              required
              data-testid="contact-subject"
              className="w-full bg-cream border border-paperline focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none px-5 py-4 text-ink"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="overline mb-3 block">Your message</label>
            <textarea
              rows={7}
              required
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              className="w-full bg-cream border border-paperline focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none px-5 py-4 text-ink resize-none"
              placeholder="Tell Victor about your audience, deadline or partnership idea."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-primary w-full sm:w-auto disabled:opacity-60"
          >
            {loading ? "Sending…" : (<><Send size={16} /> Send Message</>)}
          </button>
        </form>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-8">
          <div className="border border-paperline p-8 bg-cream-deep">
            <p className="overline mb-3">Direct</p>
            <div className="flex items-start gap-3 text-ink">
              <Mail size={18} className="mt-1 text-burgundy" />
              <a
                href={`mailto:${SITE.email}`}
                className="link-underline"
                data-testid="contact-email-link"
              >
                {SITE.email}
              </a>
            </div>
            <div className="flex items-start gap-3 text-ink mt-4">
              <MapPin size={18} className="mt-1 text-burgundy" />
              <span>{SITE.location}</span>
            </div>
          </div>

          <div className="border border-paperline p-8 bg-cream">
            <p className="overline mb-3">Newsletter</p>
            <h3 className="heading-display text-2xl font-semibold leading-tight">
              Get the dispatch.
            </h3>
            <p className="mt-2 text-ink/70 text-sm">
              New books, columns and speaking dates &mdash; occasional, never noisy.
            </p>
            <form onSubmit={subscribe} className="mt-5 space-y-3" data-testid="contact-newsletter-form">
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                data-testid="contact-newsletter-email"
                className="w-full bg-cream border border-paperline focus:border-burgundy outline-none px-4 py-3"
              />
              <button
                type="submit"
                disabled={nlLoading}
                data-testid="contact-newsletter-submit"
                className="btn-primary w-full disabled:opacity-60"
              >
                {nlLoading ? "Subscribing…" : (<>Subscribe <ArrowRight size={16} /></>)}
              </button>
            </form>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="overline mb-3 block">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-cream border border-paperline focus:border-burgundy focus:ring-1 focus:ring-burgundy outline-none px-5 py-4 text-ink"
      />
    </div>
  );
}
